/**
 * useTimeOfDay — what time of the user's life it is.
 *
 * Drives the rail's sky cap. The clock comes from the server, not the browser,
 * because the server answers in the user's *profile* timezone (so a traveller
 * still sees their own morning) and because the app has a time-travel
 * simulation the sky should follow rather than contradict.
 *
 * The phase names are the contract with theme.css — each one matches a
 * [data-sky="..."] block that re-points the --sky-* tokens.
 */

import { useServerTime } from './useServerTime'
import { useNow } from './useNow'

export type SkyPhase = 'dawn' | 'day' | 'dusk' | 'night'

export interface TimeOfDay {
  /** Feeds data-sky on the cap element. */
  phase: SkyPhase
  /** "Good morning" — no name; the caller appends it when it has one. */
  greeting: string
  /** One quiet line under the greeting. Never a task, never a number. */
  line: string
  hour: number
}

/** Boundaries are deliberately generous: dusk starts at 5pm because the
 *  feeling of the evening starts before the light actually goes. */
function phaseFor(hour: number): SkyPhase {
  if (hour >= 5 && hour < 11) return 'dawn'
  if (hour >= 11 && hour < 17) return 'day'
  if (hour >= 17 && hour < 21) return 'dusk'
  return 'night'
}

/** Copy belongs next to the phase that earns it. These lines are the only
 *  place the shell speaks to the user unprompted, so they stay
 *  non-committal: they never claim progress or imply a debt. */
const COPY: Record<SkyPhase, { greeting: string; line: string }> = {
  dawn:  { greeting: 'Good morning',   line: 'the day is still all yours' },
  day:   { greeting: 'Good afternoon', line: 'plenty of day left' },
  dusk:  { greeting: 'Good evening',   line: 'the light is going soft' },
  night: { greeting: 'Evening',        line: 'winding down is also progress' },
}

/** The server's wall-clock hour, or null if it has not answered yet.
 *
 *  Read from `time` ("06:11:10") rather than from `serverDate`, deliberately.
 *  /api/current-time reports the user's local wall clock but labels it with a
 *  +00:00 offset, so `new Date(datetime).getHours()` re-interprets 6am UTC as
 *  11pm in a UTC-7 browser and the rail draws a night sky over breakfast.
 *  The wall clock is what the sky is actually about, so take it literally and
 *  do no timezone arithmetic at all. */
function serverHour(time: string | undefined): number | null {
  if (!time) return null
  const h = Number(time.slice(0, 2))
  return Number.isInteger(h) && h >= 0 && h <= 23 ? h : null
}

export function useTimeOfDay(): TimeOfDay {
  // The sky only changes on an hour boundary, so a slow poll is plenty.
  const { serverTime } = useServerTime({ refreshInterval: 5 * 60_000 })
  // Keeps the phase advancing even if the server never answers. Fifteen
  // minutes is fine: nothing here changes faster than that.
  const clientNow = useNow(new Date(), 15 * 60_000)

  const hour = serverHour(serverTime?.time) ?? clientNow.getHours()
  const phase = phaseFor(hour)

  return { phase, hour, ...COPY[phase] }
}
